import './List.scss';

import { type FC, useEffect, useMemo, useCallback } from 'react';
import { sortBy, filter } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import * as state from 'state';
import useFontFamiliesQuery from 'queries/useFontFamiliesQuery';
import * as Core from 'components/core';
import FamilyLink from './FamilyLink';
import TrialFontsForm from './TrialFontsForm';
import ColorBar from 'components/ColorBar';
import TrialFontsButton from './TrialFontsButton';

const List: FC = () => {
  const { data, loading } = useFontFamiliesQuery();

  const { t } = useTranslation();

  // Hide the trial fonts form and scroll position when navigating away
  useEffect(() => {
    return () => {
      state.trialFontsFormVisible(false);
      state.scrolledToConcepts(false);
    };
  }, []);

  const sortedFamilies = useMemo(() => {
    if (data == null) {
      return [];
    }

    return sortBy(
      filter(data.fontFamilies, { conceptFont: false }),
      'position',
    );
  }, [data]);

  const conceptFamilies = useMemo(() => {
    if (data == null) {
      return [];
    }

    return sortBy(filter(data.fontFamilies, { conceptFont: true }), 'position');
  }, [data]);

  const conceptsCallback = useCallback((element: HTMLDivElement) => {
    if (element == null) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        state.scrolledToConcepts(entries[0]['isIntersecting']);
      },
      {
        rootMargin: '0px 0px -50% 0px',
      },
    );

    observer.observe(element);
  }, []);

  if (loading) {
    return (
      <ColorBar>
        <Core.Loader />
      </ColorBar>
    );
  }

  return (
    <>
      {sortedFamilies.map((family) => (
        <FamilyLink key={family.id} family={family} />
      ))}

      <ColorBar>
        <div className="List-trialFontButton-wrapper content-wrapper">
          <TrialFontsButton />
        </div>
      </ColorBar>

      <TrialFontsForm />

      {conceptFamilies.length > 0 && (
        <div ref={conceptsCallback}>
          <ColorBar>
            <p
              className="List-conceptsDescription"
              id="concepts"
              dangerouslySetInnerHTML={{
                __html: t('families.conceptsDescription').toString(),
              }}
            />
          </ColorBar>

          {conceptFamilies.map((family) => (
            <FamilyLink key={family.id} family={family} />
          ))}
        </div>
      )}

      <Core.ScrollToElement />
    </>
  );
};

export default List;
